import {
	FormRule,
	JobFieldMapping,
	PatientFieldMapping,
} from "@/dentlab/src/pages/form-builder/types/ruleTypes";
import { FormSection } from "@/dentlab/src/pages/form-builder/types/formTypes";
import { CustomFormData } from "@/dentconnect/src/pages/job-request/types";
import { JobEntityType, PatientEntityType } from "@/lib/supabase/supabaseTypes";

/**
 * Normalize a form field value for use as a scalar target field value:
 * - single-element string arrays unwrap to the element
 * - multi-element string arrays are joined with ", "
 * - other types pass through unchanged
 */
function normalizeScalarFromFieldValue(value: unknown): unknown {
	if (Array.isArray(value) && value.every((v) => typeof v === "string")) {
		const arr = value as string[];
		if (arr.length === 0) return "";
		if (arr.length === 1) return arr[0];
		return arr.join(", ");
	}
	return value;
}

/**
 * Maps form field values to job fields based on field mappings
 *
 * @param fieldMappings - Array of field mappings defining how to populate job fields
 * @param formData - The form submission data containing field values
 * @returns Partial job entity with populated fields
 */
export function mapFormFieldsToJobFields(
	fieldMappings: JobFieldMapping[],
	formData: CustomFormData
): Partial<JobEntityType> {
	const fieldsToUpdate: Partial<JobEntityType> = {};

	for (const mapping of fieldMappings) {
		let value: any;

		if (mapping.valueSource === "form_field" && mapping.formFieldId) {
			// Get value from form field
			const formFieldData = formData[mapping.formFieldId];
			value = normalizeScalarFromFieldValue(formFieldData?.fieldValue);
		} else if (
			mapping.valueSource === "custom" &&
			mapping.customValue !== undefined
		) {
			// Use custom value
			value = mapping.customValue;
		}

		// Trim string values to avoid whitespace issues
		if (typeof value === "string") {
			value = value.trim();
		}

		// Map job field key to actual field name
		const fieldName = mapping.jobFieldKey.split(
			"."
		)[1] as keyof JobEntityType;
		if (value !== undefined && fieldName) {
			fieldsToUpdate[fieldName] = value;
		}
	}

	return fieldsToUpdate;
}

/**
 * Maps form field values to patient fields based on field mappings
 *
 * @param fieldMappings - Array of field mappings defining how to populate patient fields
 * @param formData - The form submission data containing field values
 * @returns Partial patient entity with populated fields
 */
export function mapFormFieldsToPatientFields(
	fieldMappings: PatientFieldMapping[],
	formData: CustomFormData
): Partial<PatientEntityType> {
	const fieldsToUpdate: Partial<PatientEntityType> = {};

	for (const mapping of fieldMappings) {
		let value: any;

		if (mapping.valueSource === "form_field" && mapping.formFieldId) {
			// Get value from form field
			const formFieldData = formData[mapping.formFieldId];
			value = normalizeScalarFromFieldValue(formFieldData?.fieldValue);
		} else if (
			mapping.valueSource === "custom" &&
			mapping.customValue !== undefined
		) {
			// Use custom value
			value = mapping.customValue;
		}

		// Trim string values to avoid whitespace issues
		if (typeof value === "string") {
			value = value.trim();
		}

		// Map patient field key to actual field name
		const fieldName = mapping.patientFieldKey.split(
			"."
		)[1] as keyof PatientEntityType;
		// Exclude undefined, null, and empty strings - treat them as if the field wasn't provided
		// This allows optional fields like last_name to be NULL in the database instead of empty strings
		if (
			value !== undefined &&
			value !== null &&
			value !== "" &&
			fieldName
		) {
			fieldsToUpdate[fieldName] = value;
		}
	}

	return fieldsToUpdate;
}

export function invertRulesToFormData(
	rules: FormRule[],
	job: Partial<JobEntityType>,
	patient: Partial<PatientEntityType> | null,
	formSections: FormSection[]
): CustomFormData {
	const formData: CustomFormData = {};

	const fieldLookup = new Map<string, FormSection["fields"][number]>();
	for (const section of formSections) {
		for (const field of section.fields) {
			fieldLookup.set(field.id, field);
		}
	}

	for (const rule of rules) {
		if (!rule.enabled) continue;

		if (rule.action.type === "prefill_job_fields") {
			for (const mapping of rule.action.fieldMappings) {
				if (
					mapping.valueSource !== "form_field" ||
					!mapping.formFieldId
				)
					continue;

				const jobFieldName = mapping.jobFieldKey.split(
					"."
				)[1] as keyof JobEntityType;
				const jobValue = job[jobFieldName];

				if (jobValue !== undefined && jobValue !== null) {
					const formField = fieldLookup.get(mapping.formFieldId);
					if (formField) {
						formData[mapping.formFieldId] = {
							fieldType: formField.type,
							fieldValue: String(jobValue),
						};
					}
				}
			}
		}

		if (rule.action.type === "create_patient" && patient) {
			for (const mapping of rule.action.fieldMappings) {
				if (
					mapping.valueSource !== "form_field" ||
					!mapping.formFieldId
				)
					continue;

				const patientFieldName = mapping.patientFieldKey.split(
					"."
				)[1] as keyof PatientEntityType;
				const patientValue = patient[patientFieldName];

				if (patientValue !== undefined && patientValue !== null) {
					const formField = fieldLookup.get(mapping.formFieldId);
					if (formField) {
						formData[mapping.formFieldId] = {
							fieldType: formField.type,
							fieldValue: String(patientValue),
						};
					}
				}
			}
		}
	}

	return formData;
}
